import { palette } from '@/common/styles/palette';
import { typography } from '@/common/styles/typography';

export const themeComponents = {
  MuiTextField: {
    defaultProps: {
      slotProps: {
        inputLabel: {
          sx: {
            mb: 2,
          },
        },
      },
    },
    styleOverrides: {
      root: {
        '& .MuiInputLabel-root': {
          fontFamily: typography.fontFamily,
          fontWeight: 500,
          fontSize: '18px',
          lineHeight: '24px',
          verticalAlign: 'middle',
          color: '#000000',
          position: 'static',
          transform: 'none',
          '& .MuiFormLabel-asterisk': {
            color: palette.system.red,
          },
          '&.Mui-focused': {
            color: '#000000',
          },
          '&.Mui-error': {
            color: palette.error.main,
            '& .MuiFormLabel-asterisk': {
              color: palette.error.main,
            },
          },
          '&.Mui-disabled': {
            color: palette.text.disabled,
            '& .MuiFormLabel-asterisk': {
              color: palette.text.disabled,
            },
          },
        },
        '& .MuiOutlinedInput-root': {
          width: '100%',
          borderRadius: '4px',
          outline: 'none',
          border: '1px solid #0000003B',
          backgroundColor: 'transparent',
          fontSize: '16px',
          lineHeight: '24px',
          '&.Mui-error': {
            border: `1px solid ${palette.error.main}`,
          },
          '&.Mui-focused': {
            borderColor: palette.primary.main,
          },
          '&.MuiInputBase-readOnly': {
            backgroundColor: palette.action.disabledBackground,
            borderColor: palette.divider,
          },
          '&.MuiInputBase-multiline': {
            padding: 0,
          },
          '& .MuiOutlinedInput-input': {
            padding: '16px 12px',
            '&::placeholder': {
              opacity: 1,
              color: '#666666',
              fontFamily: typography.fontFamily,
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '0.15px',
              verticalAlign: 'middle',
            },
          },
          '& .MuiOutlinedInput-input.MuiInputBase-inputAdornedStart': {
            paddingLeft: 0,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            display: 'none',
          },
        },
      },
    },
    variants: [
      {
        props: { size: 'small' },
        style: {
          '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-input': {
              padding: '8px 12px',
            },
          },
        },
      },
    ],
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
          backgroundColor: palette.primary.main,
          color: '#ffffff !important',
        },
        '&:hover': { backgroundColor: palette.action.disabledBackground },
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      '*, *::before, *::after': {
        boxSizing: 'border-box',
      },
      html: {
        height: '100%',
        width: '100%',
      },
      body: {
        margin: 0,
        minHeight: '100%',
        fontSize: '14px',
        lineHeight: '14px',
        fontWeight: 400,
        fontFamily: typography.fontFamily,
        color: palette.text.primary,
        letterSpacing: '0px',
        backgroundColor: palette.background.default,
      },
      a: {
        color: palette.primary.main,
      },
      '& textarea': {
        width: '100%',
        outline: 'none',
        lineHeight: '20px',
        padding: 0,
        resize: 'none',
        border: `1px solid ${palette.divider}`,
        fontFamily: typography.fontFamily,
        '&::placeholder': {
          fontSize: '14px',
        },
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamily,
        color: palette.text.primary,
      },
    },
    variants: [
      {
        props: { variant: 'body1' },
        style: {
          color: palette.text.secondary,
        },
      },
    ],
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        maxWidth: '830px',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamily,
        fontWeight: 400,
        fontSize: '34px',
        lineHeight: '44px',
        letterSpacing: '0.25px',
        padding: '24px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        height: '100%',
        overflowY: 'auto',
        padding: '8px 24px',
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '20px',
        color: palette.text.secondary,
        marginLeft: '14px',
        marginRight: 0,
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          color: palette.action.disabled,
        },
      },
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: '8px',
        padding: '8px 16px',
        fontFamily: typography.fontFamily,
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '24px',
        boxSizing: 'border-box',
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        '&:not(.MuiButton-text):not(.MuiButton-loading):disabled': {
          backgroundColor: palette.action.disabledBackground,
          color: palette.action.disabled,
        },
        '&:hover': {
          boxShadow: 'none',
        },
        '&.MuiButton-loading': {
          backgroundColor: '#BDBDBD',
          color: 'transparent',
        },
        '&.MuiButton-loading:hover': {
          backgroundColor: '#BDBDBD',
        },
        '& .MuiButton-loadingIndicator': {
          color: '#FFFFFF',
        },
        '& .MuiButton-loadingIndicator .MuiCircularProgress-root': {
          width: '24px !important',
          height: '24px !important',
        },
      },
      sizeMedium: {
        padding: '8px 25px',
      },
      sizeLarge: {
        borderRadius: '8px',
        padding: '10px 22px',
        fontFamily: typography.fontFamily,
        fontWeight: 500,
        fontSize: '18px',
        lineHeight: '28px',
      },
      outlined: {
        border: `1px solid ${palette.text.tertiary}`,
        padding: '7px 15px',
      },
      outlinedSizeMedium: {
        padding: '7px 24px',
      },
      textSecondary: {
        color: palette.text.tertiary,
      },
      outlinedSecondary: {
        color: palette.text.secondary,
        borderColor: palette.text.secondary,
      },
      containedError: {
        backgroundColor: palette.error.main,
      },
      containedSuccess: {
        backgroundColor: palette.primary.main,
      },
      containedPrimary: {
        backgroundColor: palette.primary.main,
      },
    },
    variants: [
      {
        props: { variant: 'contained' },
        style: {
          fontWeight: 500,
          '&.MuiButton-loading:disabled': {
            backgroundColor: '#BDBDBD',
            color: 'transparent',
            '&:hover': {
              backgroundColor: '#BDBDBD',
            },
          },
        },
      },
      {
        props: { variant: 'outlined' },
        style: {
          border: `1px solid ${palette.text.tertiary}`,
          fontWeight: 500,
          '&:hover': {
            border: `1px solid ${palette.text.tertiary}`,
          },
          '&.Mui-disabled': {
            borderColor: palette.divider,
          },
          '&.MuiButton-loading:disabled': {
            backgroundColor: '#BDBDBD',
            color: 'transparent',
            '&:hover': {
              backgroundColor: '#BDBDBD',
            },
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'success' },
        style: {
          borderColor: palette.primary.main,
          '&:hover': {
            borderColor: palette.primary.main,
          },
          '&.MuiButton-loading:disabled': {
            backgroundColor: '#BDBDBD',
            color: 'transparent',
            borderColor: '#BDBDBD',
            '&:hover': {
              backgroundColor: '#BDBDBD',
              borderColor: '#BDBDBD',
            },
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'error' },
        style: {
          borderColor: `${palette.system.red}80`,
          '&:hover': {
            borderColor: palette.system.red,
          },
        },
      },
      {
        props: { variant: 'text' },
        style: {
          minWidth: 'initial',
          padding: '8px',
          fontFamily: typography.fontFamily,
          fontWeight: 500,
          fontSize: '16px',
          lineHeight: '24px',
        },
      },
      {
        props: { variant: 'text', size: 'small' },
        style: {
          borderRadius: '8px',
          padding: '5px 6px',
          fontFamily: typography.fontFamily,
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
    ],
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        '&.Mui-checked': {
          color: palette.primary.main,
        },
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        '&.Mui-checked': {
          color: palette.primary.main,
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: palette.background.paper,
        color: palette.text.primary,
        boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2)',
        filter:
          'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.14)) drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.12))',
        fontFamily: typography.fontFamily,
      },
      arrow: {
        color: palette.background.paper,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamily,
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.4px',
        textAlign: 'center',
        textTransform: 'none',
        '&.Mui-selected': {
          fontWeight: 500,
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
        fontSize: '16px',
        lineHeight: '24px',
      },
      select: {
        fontSize: '16px',
        lineHeight: '24px',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '18px',
        lineHeight: '26px',
        color: 'black',
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        marginRight: '8px!important',
        minWidth: 'auto!important',
        '& .MuiSvgIcon-root': {
          fontSize: '24px!important',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
        fontSize: '16px',
        lineHeight: '24px',
      },
      input: {
        fontSize: '16px',
        lineHeight: '24px',
      },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
        fontSize: '16px',
        lineHeight: '24px',
      },
      input: {
        fontSize: '16px',
        lineHeight: '24px',
        padding: '16px 12px',
      },
      notchedOutline: {
        borderRadius: '4px',
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(224, 224, 224, 1)',
        borderColor: 'rgba(224, 224, 224, 1)',
        borderRadius: 12,
        padding: 24,
        boxSizing: 'border-box',
        '& .MuiDataGrid-main': {
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        },
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '& .MuiDataGrid-row': {
          cursor: 'pointer',
        },
        '& .MuiDataGrid-overlayWrapperInner': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        },
        '& .MuiDataGrid-overlay': {
          fontFamily: typography.fontFamily,
          fontWeight: 400,
          fontSize: 18,
          lineHeight: '24px',
          textAlign: 'center',
          color: palette.text.secondary,
          paddingLeft: 24,
          paddingRight: 24,
        },
        '& .MuiDataGrid-sortButton': {
          backgroundColor: 'transparent!important',
        },
        '& .MuiDataGrid-columnHeaders': {
          borderBottomColor: 'rgba(224, 224, 224, 1)',
        },
        '& .MuiDataGrid-cell': {
          borderTopColor: 'rgba(224, 224, 224, 1)',
        },
        '& .MuiDataGrid-footerContainer': {
          borderTopColor: 'rgba(224, 224, 224, 1)',
        },
      },
      columnHeader: {
        backgroundColor: palette.secondary.main,
        padding: '12px 16px',
        borderBottom: 'none',
      },
      columnHeaderTitle: {
        fontFamily: typography.fontFamily,
        fontSize: 18,
        fontWeight: 400,
        lineHeight: '24px',
        color: palette.text.secondary,
      },
      cell: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 16,
        paddingRight: 16,
        fontSize: 16,
        lineHeight: '24px',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottomColor: 'rgba(224, 224, 224, 1)',
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      selectLabel: {
        fontFamily: typography.fontFamily,
        fontWeight: 500,
        fontSize: 14,
        lineHeight: '20px',
        color: palette.text.secondary,
        margin: 0,
      },
      select: {
        fontFamily: typography.fontFamily,
        fontWeight: 500,
        fontSize: 14,
        lineHeight: '20px',
        color: palette.text.secondary,
      },
      displayedRows: {
        fontFamily: typography.fontFamily,
        fontWeight: 500,
        fontSize: 14,
        lineHeight: '20px',
        color: '#000000',
        margin: 0,
      },
      actions: {
        color: '#000000',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
      },
      icon: {
        display: 'flex',
        alignItems: 'center',
      },
      action: {
        display: 'flex',
        alignItems: 'center',
      },
    },
  },
};
